<template>
	<div id="clientDepartments" class="jc-view">
		<JuniperPageHeader :back="'/juniper/clients/edit/' + client.id" :title="client ? client.name : ''" subtitle="Departments" />

		<div class="jdep-intro mt-2">
			Configure template and widget locations for each department page. Select a department to edit its layout and widget setup.
		</div>

		<!-- Loading Client Departments -->
		<div v-if="!loadingDepartments" class="jdep-panel mt-3">
			<div class="jdep-panel__head">
				<h5 class="mb-1">Departments</h5>
				<small>Select one department to configure widgets and template.</small>
			</div>
			<div class="jdep-departments mt-3">
				<button
					@click="selectClientDepartment(department)"
					class="jdep-department-btn"
					:class="{ 'jdep-department-btn--active': departmentSelected && department.id == departmentSelected.id }"
					v-for="department in departments"
					:key="department.id"
				>
					{{ department.title }}
				</button>
			</div>
			<div v-if="departments.length == 0" class="text-secondary mt-2">
				This client has no departments.
			</div>
		</div>
		<div v-else class="jdep-panel mt-3">
			<div class="d-flex justify-content-center p-4">
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>
		</div>

		<div v-if="departmentSelected" class="department-active">
			<div class="jdep-panel mt-3">
				<div class="section-header mb-3">
					<h5>Department Page Template</h5>
					<span>
						The department page template is an HTML <font-awesome-icon :icon="['fab', 'html5']" /> template used to build this department page.
					</span>
				</div>
				<section v-if="templates" class="department-template">
					<span>
						Select template for <b>{{ departmentSelected.title }}</b> department page
					</span>
					<div class="form-group mt-2">
						<select @change="getDepartmentTemplateWidgetsLocations()" v-model="departmentSelected.template" class="form-select jdep-template-select">
							<option v-for="template in templates" :key="template + '-key'" :value="template">{{ template }}</option>
						</select>
					</div>
				</section>
			</div>
			<div class="jdep-panel mt-3">
				<section class="department-widgets">
					<div class="section-header mb-3">
						<h5>Template Widget Locations</h5>
						<span>Add, sort, and configure widgets in each available template location.</span>
					</div>

					<div class="widget-locations-wrapper row g-3">
						<div v-for="(wLocation, l) in widgetLocations" :key="l" class="widget-location col-12 col-lg-6">
							<h4 class="text-primary">{{ wLocation.location | widgetName }}</h4>
							<div class="widgets-wrapper">
								<draggable v-model="wLocation.widgets">
									<div v-for="(widget, w) in wLocation.widgets" :key="widget.code" class="jdep-widget-item mb-2">
										<div class="left">{{ widget.name }}</div>
										<div class="right">
											<button class="btn btn-primary btn-sm me-2" @click="showWidgetConfig(widget)">Config</button>
											<button class="btn btn-danger btn-sm" @click="removeWidget(l, w)">Remove</button>
										</div>
									</div>
								</draggable>
								<p v-if="wLocation.widgets.length == 0" class="text-secondary text-center mb-3">There are no widgets in this location.</p>
								<div class="d-flex justify-content-center align-items-center">
									<button @click="showWidgetModal(wLocation)" class="btn btn-outline-primary btn-sm">Add Widget</button>
								</div>
							</div>
						</div>
						<div v-if="widgetLocations.length == 0" class="col-12">
							<p class="jdep-empty mb-0">
								Template "<b>{{ departmentSelected.template }}</b>" does not contain any widget locations.
							</p>
						</div>
					</div>
				</section>
			</div>

			<div class="jdep-panel mt-3">
				<div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
					<button @click="developerOptions = !developerOptions" class="btn btn-outline-warning" :class="{ 'btn-warning': developerOptions }">
						<font-awesome-icon :icon="['fas', 'code']" />
					</button>
					<button @click="updateClientDepartment()" class="btn btn-primary">
						<font-awesome-icon v-if="!departmentUpdating" :icon="['far', 'save']" />
						<font-awesome-icon v-else :icon="['fa', 'spinner']" class="fa-spin" />
						Save Changes
					</button>
				</div>
			</div>

			<div v-if="developerOptions" class="mt-3">
				<div class="jdep-panel">
					<div class="rounded bg-warning-lighter p-3">
						<h4 class="text-danger">Department Object</h4>
						<code>
							<pre>
							{{ departmentSelected }}
							</pre
							>
						</code>
					</div>
				</div>
			</div>
		</div>

		<div class="modal" tabindex="-1" id="widgetsListModal">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Add widget to widget location</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<h3>Available widgets</h3>
						<div class="row">
							<div v-for="(aWidget, i) in availableWidgets" :key="aWidget.code + i" class="col-md-4 mb-3">
								<div @click="addWidget(aWidget)" class="card widget-item">
									<div class="card-body">
										<div class="text-center">
											{{ aWidget.name }}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						<button type="button" class="btn btn-primary">Save changes</button>
					</div>
				</div>
			</div>
		</div>

		<div class="modal" tabindex="-1" id="widgetsConfigModal">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Config Widget</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="btn-group w-100">
							<button
								@click="widgetConfigActiveTab = 'variables'"
								class="btn btn-outline-primary"
								:class="{ 'btn-primary': widgetConfigActiveTab == 'variables' }"
								aria-current="page"
							>
								Variables
							</button>
							<button @click="widgetConfigActiveTab = 'filters'" class="btn btn-outline-primary" :class="{ 'btn-primary': widgetConfigActiveTab == 'filters' }">
								Data Filters
							</button>
						</div>
						<div class="row">
							<div class="col-12">
								<div v-if="widgetConfigActiveTab == 'variables'" id="widgetConfigVariables" class="variables shadow-sm p-3 rounded">
									<div v-for="variable in selectedWidget.variables" :key="variable.name" class="col-12">
										<template v-if="!variable.name.includes(' QY')">
											<div class="form-group mb-2">
												<label for="">{{ variable.name }}</label>
												<span class="d-block" style="font-size: 12px;" v-if="variable.name == 'BOOTSTRAP ITEM CLASS'">
													Here, you need to use Bootstrap col classes to manage items per row for the widget. For example, if we want 3 items per row on
													medium and large screens, and 2 items for mobile, we need to use the following classes: `<code>col-6 col-md-4 col-lg-4</code>`
												</span>
												<span class="d-block" style="font-size: 12px;" v-if="variable.name.includes(' COUNT')">
													Number of items that will be displayed in the widget.
												</span>
												<input type="text" class="form-control" v-model="variable.value" />
											</div>
										</template>
									</div>
								</div>
								<div v-if="widgetConfigActiveTab == 'filters'" id="widgetConfigFilters" class="variables shadow-sm p-3 rounded">
									<span class="d-block mb-3" style="font-size: 12px;"
										>Data filters are for filtering data if widget contains Events, NewsNotices, Faqs or Departments</span
									>
									<div v-for="variable in selectedWidget.variables" :key="variable.name" class="col-12">
										<template v-if="variable.name.includes(' QY')">
											<div v-if="variable.name == 'CATEGORY QY'" class="form-group mb-2">
												<label for="">{{ variable.name }}</label>
												<select class="form-select" v-model="variable.value" :multiple="!selectedWidget.name.includes('Events')">
													<template v-if="selectedWidget.code.includes('news')">
														<option v-for="nCategory in newsNoticesCategories" :value="nCategory.id" :key="nCategory.id">{{ nCategory.name }}</option>
													</template>
													<template v-if="selectedWidget.code.includes('events')">
														<option v-for="nCategory in eventsCategories" :value="nCategory.id" :key="nCategory.id">{{ nCategory.name }}</option>
													</template>
													<template v-if="selectedWidget.code.includes('faqs')">
														<option v-for="fCategory in faqsCategories" :value="fCategory.id" :key="fCategory.id">{{ fCategory.name }}</option>
													</template>
												</select>
											</div>
											<div v-else class="form-group mb-2">
												<label for="">{{ variable.name }}</label>
												<input type="text" class="form-control" v-model="variable.value" />
											</div>
										</template>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						<button type="button" class="btn btn-primary" data-bs-dismiss="modal">Save changes</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
import { Modal } from 'bootstrap'
import draggable from 'vuedraggable'
//import restApi from '../../../api/index'

export default {
	name: 'ClientDepartments',
	data() {
		return {
			developerOptions: false,
			loadingDepartments: false,
			widgetsListModal: null,
			widgetsConfigModal: null,
			loading: false,
			departmentUpdating: false,
			client: {},
			clientOptions: [],
			departments: [],
			departmentSelected: null,
			widgetLocations: [],
			selectedWidgetLocation: null,
			templates: [],
			availableWidgets: [],
			selectedWidget: [],
			newsNoticesCategories: [],
			eventsCategories: [],
			faqsCategories: [],
			widgetConfigActiveTab: 'variables' }
	},
	components: { draggable },
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']) },

	mounted() {
		this.widgetsListModal = new Modal('#widgetsListModal')
		this.widgetsConfigModal = new Modal('#widgetsConfigModal')
	},

	created() {
		this.getClient()
		this.getClientDepartments()
		this.getThemeTemplates()
		this.getClientOptions()
		this.getNewsNoticesCategories()
		this.getEventsCategories()
		this.getFaqsCategories()
	},
	methods: {
		// Get client
		getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		// Get client options
		getClientOptions() {
			this.loading = true
			juniperApi
				.post('/options/' + this.$route.params.id)
				.then(({ data }) => {
					this.clientOptions = data
					this.loading = false
					this.getThemeWidgetsList()
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		getClientDepartments() {
			this.loadingDepartments = true
			juniperApi
				.get('/departments', { params: { client_id: this.$route.params.id, include_widgets: true } })
				.then(({ data }) => {
					this.departments = data
					this.loadingDepartments = false
				})
				.catch(error => {
					this.loadingDepartments = false
					console.log(error)
				})
		},

		selectClientDepartment(department) {
			this.departmentSelected = department
			this.getDepartmentTemplateWidgetsLocations()
		},

		updateClientDepartment() {
			this.departmentUpdating = true
			if (this.departmentSelected.featured_media) {
				this.departmentSelected.featured_media = this.departmentSelected.featured_media.id
			}
			if (this.departmentSelected.icon) {
				this.departmentSelected.icon = this.departmentSelected.icon.id
			}
			if (this.departmentSelected.widgets) {
				this.departmentSelected.widgets.settings_json = this.widgetLocations
			} else {
				this.departmentSelected.widgets = { settings_json: '' }
				this.departmentSelected.widgets.settings_json = this.widgetLocations
			}

			this.departmentSelected.client_id = this.$route.params.id
			this.departmentSelected.include_widgets = true
			juniperApi
				.put('/departments/' + this.departmentSelected.id, this.departmentSelected)
				.then(({ data }) => {
					this.departmentSelected = data
					this.$swal.fire({
						icon: 'success',
						title: 'Changes successfully saved!',
						timer: 4000 })
					this.departmentUpdating = false
				})
				.catch(error => {
					this.departmentUpdating = false
					console.log(error)
				})
		},

		getThemeTemplates() {
			juniperApi
				.post('/themes/' + this.$route.params.id + '/theme-templates')
				.then(({ data }) => {
					this.templates = data.departments.single
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},

		getDepartmentTemplateWidgetsLocations() {
			juniperApi
				.post('/widgets/template/widget-locations', {
					template_path: this.clientOptions.theme_active + '/departments/single/' + this.departmentSelected.template })
				.then(({ data }) => {
					this.widgetLocations = []
					data.forEach(d => {
						let test = {
							location: d,
							widgets: [] }
						this.widgetLocations.push(test)
					})
					this.combineWidgets()
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},

		getThemeWidgetsList() {
			juniperApi
				.post('/widgets/' + this.clientOptions.theme_active + '/list')
				.then(({ data }) => {
					this.availableWidgets = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},

		combineWidgets() {
			this.widgetLocations.forEach(wLocation => {
				if (this.departmentSelected && this.departmentSelected.widgets && this.departmentSelected.widgets.settings_json) {
					let matchingItem = {}
					if (typeof this.departmentSelected.widgets.settings_json === 'string') {
						matchingItem = JSON.parse(this.departmentSelected.widgets.settings_json).find(dwLocation => dwLocation.location == wLocation.location)
					} else {
						matchingItem = this.departmentSelected.widgets.settings_json.find(dwLocation => dwLocation.location == wLocation.location)
					}

					if (matchingItem) {
						wLocation.widgets = matchingItem.widgets
					} else {
						wLocation.widgets = []
					}
				} else {
					wLocation.widgets = []
				}
			})
		},

		removeWidget(l, w) {
			this.widgetLocations[l].widgets.splice(w, 1)
		},

		showWidgetModal(location) {
			this.selectedWidgetLocation = location
			this.widgetsListModal.show()
		},

		addWidget(widget) {
			this.selectedWidgetLocation.widgets.push(widget)
			this.widgetsListModal.hide()
		},

		showWidgetConfig(widget) {
			this.selectedWidget = widget
			this.widgetsConfigModal.show()
		},

		hideWidgetConfig() {
			this.widgetsConfigModal.hide()
		},

		getNewsNoticesCategories() {
			juniperApi
				.get(`/news-notice-categories?client_id=${this.$route.params.id}`)
				.then(({ data }) => {
					this.newsNoticesCategories = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},

		getEventsCategories() {
			juniperApi
				.get(`/events-categories?client_id=${this.$route.params.id}`)
				.then(({ data }) => {
					this.eventsCategories = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},

		getFaqsCategories() {
			juniperApi
				.get(`/faq-categories?client_id=${this.$route.params.id}`)
				.then(({ data }) => {
					this.faqsCategories = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		} },
	filters: {
		widgetName: function(value) {
			if (!value) return ''
			// Remove [#sk and string ] from inputString
			let formattedString = value.replace(/\[#sk|\]|/g, '')
			formattedString = formattedString.replaceAll('-', ' ')
			formattedString = formattedString
				.split(' ')
				.map(word => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ')
			return formattedString
		} } }
</script>

<style scoped>
#clientDepartments {
	padding: 0 0 60px;
	margin-top: 0 !important;
}

.jc-view {
	width: 100%;
}

.jdep-intro {
	font-size: 13px;
	color: #5b6473;
}

.jdep-panel {
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 14px;
	padding: 16px;
}

.jdep-panel__head h5 {
	margin: 0;
	color: #0f172a;
}

.jdep-panel__head small {
	color: #64748b;
}

.jdep-departments {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
}

.jdep-department-btn {
	border: 1px solid #dbe2ea;
	background: #f8fafc;
	color: #1f2937;
	border-radius: 999px;
	padding: 8px 14px;
	font-size: 13px;
	font-weight: 600;
	transition: all 0.2s ease;
}

.jdep-department-btn:hover {
	border-color: #cbd5e1;
	background: #f1f5f9;
}

.jdep-department-btn--active {
	color: #fff;
	background: #2f6fed;
	border-color: #2f6fed;
}

.jdep-template-select {
	max-width: 360px;
}

.widget-item:hover {
	background-color: var(--bs-primary);
	color: white;
	cursor: pointer;
}

.widgets-wrapper {
	width: 100%;
	background: #f8fafc;
	border: 1px dashed #d6dee8;
	padding: 12px;
	border-radius: 12px;
}

.jdep-widget-item {
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 10px;
	padding: 10px 12px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
}

.jdep-empty {
	background: #f8fafc;
	border: 1px dashed #cbd5e1;
	border-radius: 12px;
	padding: 14px;
	color: #334155;
}

.section-header h5 {
	margin-bottom: 0;
	color: #0f172a;
}

.section-header span {
	font-size: 12px;
	padding: 0;
	color: #64748b;
	display: block;
	margin-top: 4px;
}

@media (max-width: 767px) {
	.jdep-widget-item {
		flex-direction: column;
		align-items: flex-start;
	}
}
</style>
